import type { SneakPeekDay } from '@/services/api/sneakPeek'
import { MARKS, SETTLED_MARKS } from '@/pages/sneak-peek/marks'
import { CombinationRaster } from '@/pages/sneak-peek/raster'

function shortDate(iso: string) {
  const date = new Date(`${iso}T00:00:00`)
  return `${date.getDate()}.${date.getMonth() + 1}.`
}

function markTone(mark: string | null) {
  if (mark === MARKS.ok || mark === MARKS.covered) return 'text-green-700'
  if (mark === MARKS.uncovered) return 'text-rose-700'
  if (mark === MARKS.thin) return 'text-amber-700'
  return 'text-gray-500'
}

function teacherCode(day: SneakPeekDay, name: string) {
  const index = day.names.indexOf(name)
  const listed = index >= 0 ? day.codes?.[index] : null
  if (listed) return listed
  return (day.packages || []).find((row) => row.name === name)?.code || null
}

function wholeDayNames(day: SneakPeekDay) {
  if (day.combination !== MARKS.wholeDay) return []
  if (day.whole_day_names?.length) return day.whole_day_names
  return day.whole_day_name ? [day.whole_day_name] : []
}

export function DayCell({
  day,
  onRemove,
}: {
  day: SneakPeekDay
  onRemove: () => void
}) {
  const settled = (day.lessons || []).filter(
    (lesson) => lesson.status != null && SETTLED_MARKS.has(lesson.status),
  )
  const settledOnly = !day.mark && !day.combination && !day.zero && settled.length > 0
  if (!settledOnly && (day.empty || (!day.mark && day.counts !== false))) {
    return <div className="min-h-16 rounded border border-dashed border-gray-200" />
  }
  return (
    <article className="relative w-max min-w-[12rem] rounded border bg-white p-2 text-sm">
      <header className="mb-2 border-b border-gray-100 pb-2">
        <div className="flex items-baseline justify-between gap-2">
          <time dateTime={day.date} className="font-semibold text-gray-900">{shortDate(day.date)}</time>
          {day.mark && (
            <span className={`text-[11px] font-extrabold uppercase tracking-wider ${markTone(day.mark)}`}>{day.mark}</span>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          <button
            type="button"
            aria-label={`Tag entfernen ${day.date}`}
            title="Nimmt diesen Tag aus der Auswahl. Wenn er mitgezählt hat, wird neu gerechnet."
            onClick={onRemove}
            className="rounded border px-1.5 py-0.5 text-[11px]"
          >
            Tag entfernen
          </button>
        </div>
      </header>
      {settledOnly && settled.map((lesson) => (
        <p key={lesson.entry_id}>{lesson.status}</p>
      ))}
      {day.count != null && <p>{day.count}</p>}
      <ul className="space-y-1">
        {wholeDayNames(day).map((name) => {
          const code = teacherCode(day, name)
          return (
            <li key={name} className="flex items-baseline gap-1.5 text-[12px] leading-snug text-gray-900">
              {code && (
                <span className="shrink-0 rounded bg-gray-200 px-1 py-0.5 font-mono text-[11px] font-semibold text-gray-700">
                  {code}
                </span>
              )}
              <span>{name}</span>
            </li>
          )
        })}
      </ul>
      {day.combination && (
        <div className="mt-2 border-t border-gray-200 pt-2">
          <p
            className={`text-[11px] font-extrabold uppercase tracking-wider ${
              day.combination === MARKS.doesNotCover ? 'text-rose-700' : 'text-green-700'
            }`}
          >
            {day.combination}
          </p>
          <CombinationRaster day={day} />
        </div>
      )}
    </article>
  )
}
